/**
 * Read-time mapping from a stored rule to this page's `CustomRule` — the ticket (§7)
 * asks for "mapping at read time over a destructive data migration", so every rule
 * already in the database is interpreted into the new model without being touched.
 *
 * Input is `mapFetchedRules`' output, so both pages read a rule the same way. The
 * write direction lives in `rule-writer.ts`.
 */
import { CriteriaModifier, CriteriaModifierType } from 'api-models'
import moment from 'moment'
import { ScopeType } from 'api-models/dist/advertiser/rule'
import { RuleFormType } from 'pages/workspace/asset/protection/components/rules-panel/components/rule-form/types'
import { displayCriterionValue } from './criteria'
import { AdAccountOption, CampaignOption } from './rule-entities'
import {
	ClickStatus,
	CriterionOperator,
	CustomRule,
	RuleCriterion,
	RuleLevel,
	RulePlatform,
	RuleScope
} from './types'

/** Per-rule counters for the selected window, keyed by rule id. */
export interface RuleWindowTotals {
	triggered: number
	blocked: number
}

/**
 * Stored scope → the new model's three options (plan §2.1). The two rows carrying an
 * empty `scope.values` are the old page's "this ad account" / "this campaign" cases,
 * where the target lives in the id column instead.
 *
 * | ownership   | scope.type | values | reads as              |
 * | WEBSITE     | WEBSITE    | any    | All ad traffic        |
 * | WEBSITE     | ACCOUNT    | [aa…]  | those ad accounts     |
 * | AD_ACCOUNT  | ACCOUNT    | []     | [rule.ad_account_id]  |
 * | AD_ACCOUNT  | CAMPAIGNS  | [c…]   | those campaigns       |
 * | CAMPAIGN    | CAMPAIGNS  | []     | [rule.campaign_id]    |
 *
 * What `rule-writer.ts` produces needs no branch of its own: this reads off
 * `scope.type`, not `ownership`.
 */
export const resolveRuleScope = (rule: RuleFormType): RuleScope => {
	const type = rule.trigger?.scope
	const values = rule.trigger?.scope_values ?? []

	if (type === ScopeType.ACCOUNT) {
		return {
			mode: 'accounts',
			// Empty values means the breadcrumb's ad account was the target.
			values: values.length ? values : [rule.ad_account_id].filter(Boolean)
		}
	}

	if (type === ScopeType.CAMPAIGNS) {
		return {
			mode: 'campaigns',
			values: values.length ? values : [rule.campaign_id].filter(Boolean)
		}
	}

	return { mode: 'website', values: [] }
}

/** The channel badge, derived from what the rule targets rather than defaulting to
 *  Google Ads the way the prototype did. A mixed rule falls back to the most common. */
export const resolveRulePlatform = (
	scope: RuleScope,
	adAccounts: AdAccountOption[],
	campaigns: CampaignOption[]
): RulePlatform => {
	const accountIds =
		scope.mode === 'accounts'
			? scope.values
			: scope.mode === 'campaigns'
			? campaigns
					.filter((c) => scope.values.includes(c.id))
					.map((c) => c.accountId)
			: []

	const platforms = accountIds
		.map((id) => adAccounts.find((a) => a.id === id)?.platform)
		.filter((p): p is RulePlatform => !!p)

	return platforms[0] ?? 'google-ads'
}

/** `ownership` is the level a rule was created at; the column still shows it. */
const LEVEL_BY_SCOPE: Record<RuleScope['mode'], RuleLevel> = {
	website: 'Website',
	accounts: 'Ad account',
	campaigns: 'Campaign'
}

/** `focus.source` is stored as a `SourceType` enum; the drawer's dropdown is keyed by
 *  the words it shows. Without this an existing rule opened with "From the same"
 *  EMPTY, and all three trigger fields are mandatory. */
const FOCUS_BY_STORED: Record<string, string> = {
	VISITOR: 'Visitor',
	IP: 'IP address',
	RANGE: 'IP range',
	PLACEMENT: 'Placement',
	DEVICE: 'Device'
}

const CLICK_STATUS_BY_STORED: Record<string, ClickStatus> = {
	GOOD: 'Valid',
	SUSPICIOUS: 'Suspicious',
	BAD: 'Bad'
}

/** Stored period units are singular and include `second`, which the page's dropdown
 *  does not offer, so such a rule is carried up to the nearest unit that exists —
 *  90 seconds reading as "90 minutes" would misstate it by two orders of magnitude. */
const PERIOD_UNIT_BY_STORED: Record<string, 'minutes' | 'hours' | 'days'> = {
	second: 'minutes',
	minute: 'minutes',
	hour: 'hours',
	day: 'days'
}

/** Carries the VALUE up with the unit, since the two are edited and saved together:
 *  relabelling `second` alone would widen the period 60x on the next save. Rounds up,
 *  so a sub-minute window never carries up to zero. */
const toDisplayPeriod = <T extends number | ''>(
	value: T,
	stored: string | undefined,
	fallbackUnit: 'minutes' | 'hours' | 'days'
): { value: T | number; unit: 'minutes' | 'hours' | 'days' } => ({
	value:
		stored === 'second' && typeof value === 'number'
			? Math.max(1, Math.ceil(value / 60))
			: value,
	unit: PERIOD_UNIT_BY_STORED[stored ?? ''] ?? fallbackUnit
})

/** One stored modifier → one criterion row, or `null` for one this page has no row
 *  for: a rule carrying something unknown must still list and still fire. */
const toCriterion = (modifier: CriteriaModifier): RuleCriterion | null => {
	const operatorFor = (negative?: boolean): CriterionOperator =>
		negative ? 'is not' : 'is'
	const comparatorFor = (operand?: string): CriterionOperator =>
		operand === 'less' ? 'less than' : 'more than'

	switch (modifier.type) {
		case CriteriaModifierType.NO_DEVICE:
			return { id: 'bounced', values: [] }
		case CriteriaModifierType.BAD_CLICKS:
			return { id: 'bad-clicks', values: [] }
		case CriteriaModifierType.PROXY:
			return { id: 'proxy', values: [] }

		case CriteriaModifierType.THREAT_LEVEL:
			return {
				id: 'threat-level',
				operator: operatorFor(modifier.threat_level?.negative),
				values: (modifier.threat_level?.values ?? []).map((value) =>
					displayCriterionValue('threat-level', value)
				)
			}

		case CriteriaModifierType.IP_USAGE:
			// `mapFetchedRules` has already renamed hosting/corporate/consumer.
			return {
				id: 'ip-type',
				operator: operatorFor(modifier.ip_usage?.negative),
				values: modifier.ip_usage?.values ?? []
			}

		case CriteriaModifierType.DEVICE_TYPE:
			return {
				id: 'device-type',
				operator: operatorFor(modifier.device_type?.negative),
				values: (modifier.device_type?.values ?? []).map((value) =>
					displayCriterionValue('device-type', value)
				)
			}

		case CriteriaModifierType.COUNTRY:
			// Stored as ISO codes; the picker speaks names. Resolved by `toCustomRule`.
			return {
				id: 'country',
				operator: operatorFor(modifier.country?.negative),
				values: modifier.country?.values ?? []
			}

		case CriteriaModifierType.TIME:
			return {
				id: 'time-on-site',
				operator: comparatorFor(modifier.time?.operand),
				values: [String(modifier.time?.primary?.value ?? '')]
			}

		case CriteriaModifierType.PAGE_VIEWS:
			return {
				id: 'page-views',
				operator: comparatorFor(modifier.page_views?.operand),
				values: [String(modifier.page_views?.primary ?? '')]
			}

		case CriteriaModifierType.BOT_PROBABILITY:
			return {
				id: 'bot-probability',
				operator: operatorFor(modifier.bot_probability?.negative),
				values: (modifier.bot_probability?.values ?? []).map((value) =>
					displayCriterionValue('bot-probability', value)
				)
			}

		case CriteriaModifierType.INTERACTION:
			// `criteria.ts` offers exactly the four stored levels (plan §6 Q4).
			return {
				id: 'interaction',
				operator: operatorFor(modifier.user_interaction?.negative),
				values: (modifier.user_interaction?.values ?? []).map((value) =>
					displayCriterionValue('interaction', value)
				)
			}

		case CriteriaModifierType.CONVERSION:
			return {
				id: 'conversion',
				operator: 'is',
				values: modifier.conversion?.label ? [modifier.conversion.label] : []
			}
		case CriteriaModifierType.NOT_CONVERSION:
			// The stored model splits the pair into two modifiers; the page shows one row.
			return {
				id: 'conversion',
				operator: 'is not',
				values: modifier.conversion?.label ? [modifier.conversion.label] : []
			}

		case CriteriaModifierType.SEARCH_KEYWORD:
			// Stored as a single string, so a rule arrives as a one-chip list (plan §4.2.1a).
			return {
				id: 'keyword',
				operator: operatorFor(modifier.keyword_text?.negative),
				values: modifier.keyword_text?.value
					? [modifier.keyword_text.value]
					: []
			}

		default:
			return null
	}
}

export interface ToCustomRuleContext {
	adAccounts: AdAccountOption[]
	campaigns: CampaignOption[]
	/** ISO alpha-2 → display name, for the country criterion. */
	countryNameByCode: Map<string, string>
	totals?: RuleWindowTotals
	/** Rule id → when it last fired. The map ABSENT means the query has not answered;
	 *  a `null` VALUE means "never fired in the lookback window". */
	lastExecutionByRuleId?: Map<string, string | null | undefined>
}

/** A stored rule, as this page's table and ledger want it. */
export const toCustomRule = (
	rule: RuleFormType,
	context: ToCustomRuleContext
): CustomRule => {
	const scope = resolveRuleScope(rule)
	const criteria = (rule.modifiers ?? [])
		.map(toCriterion)
		.filter((c): c is RuleCriterion => c !== null)
		.map((criterion) =>
			criterion.id === 'country'
				? {
						...criterion,
						values: criterion.values.map(
							(code) => context.countryNameByCode.get(code) ?? code
						)
				  }
				: criterion
		)

	const triggerPeriod = toDisplayPeriod(
		rule.trigger?.period_value ?? '',
		rule.trigger?.period_type,
		'hours'
	)
	const blacklistPeriod = toDisplayPeriod(
		rule.block?.period?.value ?? 1,
		rule.block?.period?.unit,
		'days'
	)

	return {
		id: rule.id ?? '',
		name: rule.name ?? '',
		description: rule.description ?? '',
		scope,
		platform: resolveRulePlatform(scope, context.adAccounts, context.campaigns),
		level: LEVEL_BY_SCOPE[scope.mode],
		status: rule.enabled ? 'active' : 'paused',
		trigger: {
			maxClicks: rule.trigger?.max_ad_clicks ?? '',
			periodValue: triggerPeriod.value,
			periodUnit: triggerPeriod.unit,
			focus: FOCUS_BY_STORED[rule.trigger?.focus ?? ''] ?? ''
		},
		criteria,
		threshold: {
			mode: rule.threshold?.type === 'percentage' ? 'percentage' : 'all',
			percent: rule.threshold?.percentage_value ?? ''
		},
		actions: {
			clickStatus: CLICK_STATUS_BY_STORED[rule.resolve_status] ?? '',
			addLabels: !!rule.resolve_labels?.enabled,
			labels: (rule.resolve_labels?.labels ?? []).join(', '),
			blacklist: !!rule.block,
			blacklistValue: blacklistPeriod.value,
			blacklistUnit: blacklistPeriod.unit,
			tempIpExclusion: !!rule.temp_ip_exclusion,
			tempIpExclusionPeriod: rule.temp_ip_exclusion?.period,
			audienceExclusion: !!rule.audience_exclusion
		},
		triggered: context.totals?.triggered ?? 0,
		blocked: context.totals?.blocked ?? 0,
		updatedAt: '',
		lastExecutedAt: context.lastExecutionByRuleId
			? formatLastExecution(context.lastExecutionByRuleId.get(rule.id ?? ''))
			: LAST_EXECUTION_UNKNOWN
	}
}

/** Shown while the executions query is in flight — distinct from 'Never'. */
export const LAST_EXECUTION_UNKNOWN = '—'

/** `Last execution`, as the column reads it. A date-only value keeps its time off:
 *  demo mode carries day granularity, and "00:00" would invent precision. */
export const formatLastExecution = (value?: string | null): string => {
	if (!value) return 'Never'
	const at = moment(value)
	if (!at.isValid()) return 'Never'

	const day = at.isSame(moment(), 'day')
		? 'Today'
		: at.isSame(moment().subtract(1, 'day'), 'day')
		? 'Yesterday'
		: at.format('MMM D')

	const hasTime = value.includes('T') || value.includes(' ')
	return hasTime ? `${day}, ${at.format('HH:mm')}` : day
}
